/**
 * Canva Basics, a 5-Step Cheat Sheet: /resources/canva/basics
 *
 * The five steps, their copy, the two reference boxes, the tip and the
 * completion panel are Daniel's, verbatim.
 *
 * THREE THINGS CHANGED FROM THE HTML AS SUPPLIED, all of them deliberate:
 *
 *   1. The house stylesheet, not the one that came with it. The original was
 *      built on Playfair Display, Source Sans 3, DM Sans and #C4704B — a
 *      typeface set and a terracotta that exist nowhere else on this site —
 *      with 2px and 3px corners. That combination is exactly what made the
 *      old guides read as a different product, and removing it is what
 *      GuideShell, buttonStyles and RADIUS_CSS were all built to do. Shipping
 *      it back in would undo that in one file.
 *   2. `aitools.carenglasser.com` on the completion button does not resolve.
 *      It is an address from before the Smart Stack domains — the same dead
 *      host that had the Claude app's own step pointing nowhere until August.
 *      It goes to aitools.yoursmartstack.com.
 *   3. No "Created by Caren Glasser, The Tech Evangelist" footer. Every other
 *      guide just had that line removed so SiteFooter is the only footer on
 *      the site; adding one back here would be the odd page out.
 *
 * The .formula-box the original used maps onto .principle-box, which is the
 * same object in the house stylesheet: a labelled aside with a rule down the
 * left. Say the word and the original CSS goes back in; it is one file.
 */

import GuideShell from "@/components/GuideShell";
import GuideFrame from "@/components/GuideFrame";

const HTML_SRC = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Canva Basics: 5-Step Cheat Sheet | Caren Glasser</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,400;1,9..144,600&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">

<style>
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --cream: oklch(0.98 0.008 85);
    --linen: oklch(0.93 0.01 85);
    --espresso: oklch(0.22 0.015 65);
    --terracotta: oklch(0.32 0.08 255);
    --border: oklch(0.88 0.012 85);
    --white: oklch(1 0.004 85);
    --terracotta-light: oklch(0.32 0.08 255 / 0.08);
    --terracotta-mid: oklch(0.32 0.08 255 / 0.22);
    --muted: oklch(0.52 0.015 65);
    --body: oklch(0.52 0.015 65);
  }

  body {
    background: var(--cream);
    color: var(--espresso);
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    min-height: 100vh;
    padding: 40px 20px 60px;
  }

  .container { max-width: 680px; margin: 0 auto; }

  .header {
    text-align: center;
    margin-bottom: 48px;
    padding-bottom: 36px;
    border-bottom: 1px solid var(--border);
  }

  .header .eyebrow {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--terracotta);
    margin-bottom: 14px;
  }

  .header h1 {
    font-family: 'Fraunces', Georgia, serif;
    font-size: clamp(28px, 5vw, 38px);
    font-weight: 700;
    color: var(--espresso);
    line-height: 1.2;
    margin-bottom: 14px;
  }

  .header p {
    font-size: 16px;
    color: var(--muted);
    line-height: 1.6;
    max-width: 480px;
    margin: 0 auto 20px;
  }

  .progress-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-top: 20px;
  }

  .progress-bar {
    width: 200px;
    height: 4px;
    background: var(--border);
    border-radius: 2px;
    overflow: hidden;
  }

  .progress-fill {
    height: 100%;
    background: var(--terracotta);
    border-radius: 2px;
    transition: width 0.5s ease;
    width: 0%;
  }

  .progress-text {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    color: oklch(0.52 0.015 65);
  }

  .step {
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 3px;
    margin-bottom: 16px;
    overflow: hidden;
    transition: all 0.3s ease;
  }

  .step.completed {
    border-color: var(--terracotta-mid);
    background: var(--terracotta-light);
  }

  .step-header {
    display: flex;
    align-items: flex-start;
    gap: 18px;
    padding: 24px 28px;
    cursor: pointer;
    user-select: none;
  }

  .step-check {
    width: 28px;
    height: 28px;
    border: 2px solid var(--border);
    border-radius: 50%;
    flex-shrink: 0;
    margin-top: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    background: var(--white);
  }

  .step.completed .step-check {
    background: var(--terracotta);
    border-color: var(--terracotta);
  }

  .step-check svg { opacity: 0; transform: scale(0.5); transition: all 0.25s ease; }
  .step.completed .step-check svg { opacity: 1; transform: scale(1); }

  .step-title-wrap { flex: 1; }

  .step-number {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--terracotta);
    margin-bottom: 4px;
  }

  .step.completed .step-number { color: oklch(0.26 0.075 255); }

  .step-title {
    font-family: 'Fraunces', Georgia, serif;
    font-size: 20px;
    font-weight: 600;
    color: var(--espresso);
    line-height: 1.3;
  }

  .step-toggle {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 20px;
    color: var(--border);
    flex-shrink: 0;
    line-height: 1;
    margin-top: 4px;
    transition: transform 0.3s ease, color 0.3s ease;
  }

  .step.open .step-toggle { transform: rotate(45deg); color: var(--terracotta); }

  .step-body { display: none; padding: 0 28px 28px 74px; }
  .step.open .step-body { display: block; }

  .step-body p {
    font-size: 16px;
    line-height: 1.7;
    color: var(--body);
    margin-bottom: 14px;
  }

  .step-body ul { list-style: none; margin-bottom: 16px; }

  .step-body ul li {
    font-size: 16px;
    line-height: 1.6;
    color: var(--body);
    padding: 5px 0 5px 20px;
    position: relative;
  }

  .step-body ul li::before {
    content: "—";
    position: absolute;
    left: 0;
    color: var(--terracotta);
    font-weight: 300;
  }

  .principle-box {
    background: var(--linen);
    border-left: 3px solid var(--terracotta);
    border-radius: 0 3px 3px 0;
    padding: 14px 18px;
    margin: 12px 0 16px;
  }

  .principle-box .label {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--terracotta);
    margin-bottom: 6px;
  }

  .principle-box p {
    font-size: 14px;
    color: var(--espresso);
    line-height: 1.6;
    margin: 0;
    font-style: italic;
  }

  .mark-done-btn {
    display: inline-block;
    margin-top: 8px;
    padding: 10px 22px;
    background: var(--terracotta);
    color: var(--white);
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.03em;
    border: none;
    border-radius: 2px;
    cursor: pointer;
    transition: background 0.2s ease;
  }

  .mark-done-btn:hover { background: oklch(0.26 0.075 255); }
  .step.completed .mark-done-btn { background: oklch(0.26 0.075 255); }

  .tip-box {
    background: var(--linen);
    border: 1px solid var(--border);
    border-radius: 3px;
    padding: 24px 28px;
    margin-top: 8px;
    display: flex;
    gap: 16px;
    align-items: flex-start;
  }

  .tip-icon {
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    margin-top: 1px;
    color: var(--terracotta);
  }

  .tip-box p {
    font-size: 16px;
    line-height: 1.7;
    color: var(--body);
    margin: 0;
  }

  .completion {
    display: none;
    text-align: center;
    padding: 40px 28px;
    background: var(--white);
    border: 1px solid var(--terracotta-mid);
    border-radius: 3px;
    margin-top: 8px;
  }

  .completion.visible { display: block; }

  .completion .icon {
    width: 48px;
    height: 48px;
    margin: 0 auto 16px;
    color: var(--terracotta);
  }

  .completion h2 {
    font-family: 'Fraunces', Georgia, serif;
    font-size: 24px;
    color: var(--espresso);
    margin-bottom: 10px;
  }

  .completion p {
    font-size: 16px;
    color: var(--muted);
    line-height: 1.6;
    max-width: 420px;
    margin: 0 auto 20px;
  }

  .completion a {
    display: inline-block;
    padding: 12px 28px;
    background: var(--terracotta);
    color: var(--white);
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 14px;
    font-weight: 600;
    border-radius: 2px;
    text-decoration: none;
    transition: background 0.2s;
  }

  .completion a:hover { background: oklch(0.26 0.075 255); }

  .footer {
    text-align: center;
    margin-top: 48px;
    padding-top: 28px;
    border-top: 1px solid var(--border);
  }

  .footer p {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    color: oklch(0.88 0.012 85);
    letter-spacing: 0.04em;
  }

  .footer a { color: var(--terracotta); text-decoration: none; }
</style>
</head>
<body>
<div class="container">

  <div class="header">
    <p class="eyebrow">Resource Center: Quick Reference</p>
    <h1>Canva Basics<br>5-Step Cheat Sheet</h1>
    <p>Check off each step as you work through it. By the end you'll have your first social image ready to post, all on the free plan.</p>
    <div class="progress-wrap">
      <div class="progress-bar"><div class="progress-fill" id="progressFill"></div></div>
      <span class="progress-text" id="progressText">0 of 5 complete</span>
    </div>
  </div>

  <!-- STEP 1 -->
  <div class="step open" id="step1">
    <div class="step-header" onclick="toggleStep('step1')">
      <div class="step-check">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="step-title-wrap">
        <div class="step-number">Step 01</div>
        <div class="step-title">Set Up Your Free Canva Account</div>
      </div>
      <div class="step-toggle">+</div>
    </div>
    <div class="step-body">
      <p>Everything in this guide works on Canva's free plan, no credit card, no upgrade required. Start here.</p>
      <ul>
        <li>Go to <strong>canva.com</strong> and click "Sign up free"</li>
        <li>Create your account with your email, Google, or Facebook login</li>
        <li>When asked what you'll use Canva for, choose <strong>"Personal"</strong> or <strong>"Small Business"</strong></li>
        <li>You'll land on your Home Dashboard, this is where all your designs live</li>
        <li>No design experience needed. You're not starting from a blank page, you're starting from a template</li>
      </ul>
      <p>That's it. You're in. Everything else happens from here.</p>
      <button class="mark-done-btn" onclick="markDone('step1')">✓ Mark as done</button>
    </div>
  </div>

  <!-- STEP 2 -->
  <div class="step" id="step2">
    <div class="step-header" onclick="toggleStep('step2')">
      <div class="step-check">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="step-title-wrap">
        <div class="step-number">Step 02</div>
        <div class="step-title">Choose the Right Template for Social</div>
      </div>
      <div class="step-toggle">+</div>
    </div>
    <div class="step-body">
      <p>Canva has thousands of templates. The trick is knowing which size to start with so your image looks right on the platform you're posting to.</p>
      <ul>
        <li>From your dashboard, click <strong>"Create a design"</strong> in the top right corner</li>
        <li>Search for the format you need, try "Instagram Post," "LinkedIn Post," or "Facebook Post"</li>
        <li>Canva will open a blank canvas at exactly the right size</li>
        <li>Click <strong>"Templates"</strong> on the left panel to browse free options, filter by "Free" to avoid Pro templates</li>
        <li>Click any template to apply it, you can always swap it out later</li>
      </ul>
      <div class="principle-box">
        <div class="label">Quick Size Reference</div>
        <p>Instagram Post: 1080 &times; 1080 &nbsp;&middot;&nbsp; LinkedIn Post: 1200 &times; 627 &nbsp;&middot;&nbsp; Facebook Post: 1200 &times; 630</p>
        <p>When in doubt, the Instagram square (1080&times;1080) works well across most platforms.</p>
      </div>
      <button class="mark-done-btn" onclick="markDone('step2')">✓ Mark as done</button>
    </div>
  </div>

  <!-- STEP 3 -->
  <div class="step" id="step3">
    <div class="step-header" onclick="toggleStep('step3')">
      <div class="step-check">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="step-title-wrap">
        <div class="step-number">Step 03</div>
        <div class="step-title">Make It Yours: Text, Colors &amp; Fonts</div>
      </div>
      <div class="step-toggle">+</div>
    </div>
    <div class="step-body">
      <p>This is where the template becomes your design. You're not redesigning anything, just swapping in your words and colors.</p>
      <ul>
        <li><strong>Edit text:</strong> Double-click any text block to edit it, type your headline, tagline, or message</li>
        <li><strong>Change font:</strong> Highlight your text, then use the font menu at the top to pick something that feels like you. Stick to one or two fonts maximum</li>
        <li><strong>Change colors:</strong> Click any colored element, then click the color swatch at the top to open the color picker. Enter your brand hex code if you have one</li>
        <li><strong>Resize text:</strong> Use the size number at the top to make text bigger or smaller</li>
        <li><strong>Move elements:</strong> Click and drag anything to reposition it on the canvas</li>
      </ul>
      <p>Keep it simple. One clear headline, your brand colors if you have them, and plenty of breathing room. Less is more on social.</p>
      <button class="mark-done-btn" onclick="markDone('step3')">✓ Mark as done</button>
    </div>
  </div>

  <!-- STEP 4 -->
  <div class="step" id="step4">
    <div class="step-header" onclick="toggleStep('step4')">
      <div class="step-check">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="step-title-wrap">
        <div class="step-number">Step 04</div>
        <div class="step-title">Swap the Images</div>
      </div>
      <div class="step-toggle">+</div>
    </div>
    <div class="step-body">
      <p>Most templates come with placeholder images. Swap them out with something that fits your brand, using Canva's free photo library or your own photos.</p>
      <ul>
        <li><strong>Use Canva's free photos:</strong> Click <strong>"Elements"</strong> in the left panel, then search "Photos". Thousands of free images are available</li>
        <li><strong>Upload your own photo:</strong> Click <strong>"Uploads"</strong> in the left panel, then "Upload files", then select an image from your device</li>
        <li><strong>Replace a template image:</strong> Drag your new photo directly onto the existing image, Canva will swap it in automatically</li>
        <li><strong>Adjust the crop:</strong> Double-click the image to reposition it inside the frame, drag to find the right framing</li>
        <li><strong>Keep it relevant:</strong> The image should support your message, not distract from it</li>
      </ul>
      <p>Your own photo, even a simple headshot or workspace shot, will always perform better than a stock image. Use what you have.</p>
      <button class="mark-done-btn" onclick="markDone('step4')">✓ Mark as done</button>
    </div>
  </div>

  <!-- STEP 5 -->
  <div class="step" id="step5">
    <div class="step-header" onclick="toggleStep('step5')">
      <div class="step-check">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="step-title-wrap">
        <div class="step-number">Step 05</div>
        <div class="step-title">Download and Post</div>
      </div>
      <div class="step-toggle">+</div>
    </div>
    <div class="step-body">
      <p>Your design saves automatically as you work. When you're ready to download it, here's how.</p>
      <ul>
        <li>Click the <strong>"Share"</strong> button in the top right corner</li>
        <li>Select <strong>"Download"</strong> from the dropdown</li>
        <li>Choose <strong>PNG</strong> for social media images, it gives you the best quality</li>
        <li>Click the purple <strong>"Download"</strong> button, the file saves to your device</li>
        <li>Head to your platform of choice and upload it like any other image</li>
      </ul>
      <div class="principle-box">
        <div class="label">File Format Guide</div>
        <p>PNG &rarr; social media images &nbsp;&middot;&nbsp; JPG &rarr; photos &nbsp;&middot;&nbsp; PDF &rarr; anything you'll print</p>
        <p>PNG is the right choice almost every time for social posts. It keeps your text sharp and your colors clean.</p>
      </div>
      <p>That's your first Canva design, done. Now duplicate it, change the text, and you have your second one in under two minutes.</p>
      <button class="mark-done-btn" onclick="markDone('step5')">✓ Mark as done</button>
    </div>
  </div>

  <!-- TIP -->
  <div class="tip-box">
    <svg class="tip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26C17.81 13.47 19 11.38 19 9c0-3.87-3.13-7-7-7z"/>
      <path d="M9 21h6M10 17v-2M14 17v-2"/>
    </svg>
    <p><strong>One thing to remember:</strong> You don't need to be a designer to use Canva well. You need to be decisive. Pick a template, make it yours, and post it. Done is better than perfect, especially when you can always go back and improve it next time.</p>
  </div>

  <!-- COMPLETION -->
  <div class="completion" id="completion">
    <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
    <h2>Your first design is ready.</h2>
    <p>You've worked through all 5 steps. Now go post it, that's where the confidence actually comes from.</p>
    <a href="https://aitools.yoursmartstack.com/" target="_blank">Explore the Resource Center →</a>
  </div>

</div>

<script>
  const steps = ['step1','step2','step3','step4','step5'];

  function toggleStep(id) {
    const el = document.getElementById(id);
    const isOpen = el.classList.contains('open');
    steps.forEach(s => document.getElementById(s).classList.remove('open'));
    if (!isOpen) el.classList.add('open');
  }

  function markDone(id) {
    const el = document.getElementById(id);
    el.classList.add('completed');
    el.classList.remove('open');
    updateProgress();
  }

  function updateProgress() {
    const done = steps.filter(s => document.getElementById(s).classList.contains('completed')).length;
    const pct = (done / steps.length) * 100;
    document.getElementById('progressFill').style.width = pct + '%';
    document.getElementById('progressText').textContent = done + ' of ' + steps.length + ' complete';
    if (done === steps.length) {
      document.getElementById('completion').classList.add('visible');
    }
  }

  /* No toggleStep('step1') call here, deliberately. Step 1 already carries the
     open class in the markup, so calling toggle CLOSES it — every one of these
     guides loaded with all its cards shut and nothing to show that a card opens
     at all. The markup is the intent. Fixed 4 Sep 2026. */
<\/script>
</body>
</html>
`;

export default function CanvaBasics() {
  return (
    <GuideShell
      context={"Canva guide"}
      backHref={"/resources/canva"}
      backLabel={"Back to Canva"}
    >
      <GuideFrame html={HTML_SRC} title={"Canva Basics: 5-Step Cheat Sheet"} />
    </GuideShell>
  );
}
